import React, { useEffect, useMemo, useState } from 'react';
import { getCategoryColor } from '../../utils/categories';
import { formatCurrency } from '../../utils/formatCurrency';
import { formatSpecificationDisplay } from '../../utils/specifications';
import {
  buildModificationForest,
  countChildren,
  type HierarchyNode,
} from '../../utils/modificationHierarchy';
import type { VehicleDetailModification } from './types';
import { ModThumb } from './ModThumb';

interface ModificationNestedCardsProps {
  modifications: VehicleDetailModification[];
  showPricing?: boolean;
  isOwner?: boolean;
  onEdit?: (modification: VehicleDetailModification) => void;
  onDelete?: (id: string) => void;
  onMove?: (modification: VehicleDetailModification) => void;
  showSpecs?: boolean;
}

function nodeKey(node: HierarchyNode<VehicleDetailModification>): string {
  return String(node.mod.id || `i:${node.index}`);
}

function findNodeByPath(
  forest: HierarchyNode<VehicleDetailModification>[],
  path: string[]
): HierarchyNode<VehicleDetailModification> | null {
  if (path.length === 0) return null;
  let level = forest;
  let current: HierarchyNode<VehicleDetailModification> | null = null;
  for (const key of path) {
    current = level.find((n) => nodeKey(n) === key) ?? null;
    if (!current) return null;
    level = current.children;
  }
  return current;
}

function OwnerActions({
  mod,
  onEdit,
  onDelete,
  onMove,
}: {
  mod: VehicleDetailModification;
  onEdit?: (modification: VehicleDetailModification) => void;
  onDelete?: (id: string) => void;
  onMove?: (modification: VehicleDetailModification) => void;
}) {
  if (!onEdit && !onDelete && !onMove) return null;

  const handleDelete = () => {
    if (onDelete && window.confirm(`Are you sure you want to delete "${mod.title}"?`)) {
      onDelete(mod.id);
    }
  };

  return (
    <div
      className="flex shrink-0 gap-1 opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      {onMove && (
        <button
          type="button"
          onClick={() => onMove(mod)}
          className="p-2 text-gray-400 hover:text-orange-300 hover:bg-white/[0.04] rounded-lg min-h-[40px] min-w-[40px] cursor-pointer"
          title="Move"
          aria-label={`Move ${mod.title}`}
        >
          <svg className="w-4 h-4 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
          </svg>
        </button>
      )}
      {onEdit && (
        <button
          type="button"
          onClick={() => onEdit(mod)}
          className="p-2 text-gray-400 hover:text-orange-300 hover:bg-white/[0.04] rounded-lg min-h-[40px] min-w-[40px] cursor-pointer"
          title="Edit"
          aria-label={`Edit ${mod.title}`}
        >
          <svg className="w-4 h-4 mx-auto" fill="currentColor" viewBox="0 0 24 24">
            <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
          </svg>
        </button>
      )}
      {onDelete && (
        <button
          type="button"
          onClick={handleDelete}
          className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg min-h-[40px] min-w-[40px] cursor-pointer"
          title="Delete"
          aria-label={`Delete ${mod.title}`}
        >
          <svg className="w-4 h-4 mx-auto" fill="currentColor" viewBox="0 0 24 24">
            <path
              fillRule="evenodd"
              d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      )}
    </div>
  );
}

function PartCard({
  node,
  showPricing,
  isOwner,
  onEdit,
  onDelete,
  onMove,
  showSpecs,
  onOpen,
  costIncluded,
}: {
  node: HierarchyNode<VehicleDetailModification>;
  showPricing?: boolean;
  isOwner?: boolean;
  onEdit?: (modification: VehicleDetailModification) => void;
  onDelete?: (id: string) => void;
  onMove?: (modification: VehicleDetailModification) => void;
  showSpecs?: boolean;
  onOpen?: () => void;
  costIncluded?: boolean;
}) {
  const mod = node.mod;
  const childTotal = countChildren(node);
  const hasKids = node.children.length > 0;
  const categoryColor = getCategoryColor(mod.category);
  const specs = showSpecs ? formatSpecificationDisplay(mod.specifications) : [];
  const previewKids = node.children.slice(0, 3).map((c) => c.mod.title);
  const moreKids = Math.max(0, node.children.length - previewKids.length);
  const interactive = Boolean(onOpen && hasKids);

  const body = (
    <>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1 flex items-start gap-3">
          <ModThumb url={mod.image_url} alt="" size="md" />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 mb-2">
              <span className="inline-flex items-center gap-1.5 text-meta">
                <span
                  className="h-1.5 w-1.5 rounded-full shrink-0"
                  style={{ backgroundColor: categoryColor }}
                  aria-hidden
                />
                {mod.category}
              </span>
              {hasKids && (
                <span className="text-xs text-orange-300/90 font-medium tabular-nums">
                  {childTotal} inside
                </span>
              )}
            </div>
            <h3 className="heading-card text-lg leading-snug">{mod.title}</h3>
            {mod.vendor && <p className="text-sm text-muted mt-1">{mod.vendor}</p>}
          </div>
        </div>
        {isOwner && <OwnerActions mod={mod} onEdit={onEdit} onDelete={onDelete} onMove={onMove} />}
      </div>

      {showPricing && (
        <div className="mt-4 flex items-center justify-between gap-3">
          {costIncluded || mod.parentComponent ? (
            <span className="text-meta">Included in parent</span>
          ) : (
            <>
              <span className="text-meta">Invested</span>
              <span className="text-base text-white font-semibold tabular-nums tracking-tight">
                {mod.cost == null || mod.cost === 0 ? '-' : formatCurrency(mod.cost)}
              </span>
            </>
          )}
        </div>
      )}

      {mod.description && (
        <p className="mt-3 text-sm text-muted leading-relaxed line-clamp-2">{mod.description}</p>
      )}

      {specs.length > 0 && (
        <ul className="mt-3 space-y-1 border-t border-white/[0.06] pt-3">
          {specs.slice(0, 4).map((line) => (
            <li key={line} className="text-meta leading-snug">
              {line}
            </li>
          ))}
          {specs.length > 4 && (
            <li className="text-meta">+{specs.length - 4} more</li>
          )}
        </ul>
      )}

      {hasKids && (
        <div className="mt-4 pt-3 border-t border-white/[0.06]">
          <ul className="space-y-1 mb-3">
            {previewKids.map((title) => (
              <li key={title} className="text-sm text-gray-300 truncate flex items-center gap-2">
                <span className="h-px w-2.5 bg-[#1a1a1a] shrink-0" aria-hidden />
                {title}
              </li>
            ))}
            {moreKids > 0 && (
              <li className="text-meta pl-4">+{moreKids} more</li>
            )}
          </ul>
          <span className="inline-flex items-center gap-1.5 text-sm font-medium text-orange-300 group-hover:text-orange-200 transition-colors">
            View parts inside
            <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </span>
        </div>
      )}
    </>
  );

  const shellClass =
    'relative group min-w-0 build-section overflow-hidden transition-colors duration-200 hover:border-white/[0.12]/90';

  return (
    <article
      className={`${shellClass}${interactive ? ' cursor-pointer focus-within:ring-2 focus-within:ring-orange-500/40' : ''}`}
      onClick={interactive ? onOpen : undefined}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onOpen?.();
              }
            }
          : undefined
      }
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      aria-label={interactive ? `Open ${mod.title}, ${childTotal} parts inside` : undefined}
    >
      <span
        className="absolute inset-y-0 left-0 w-[3px]"
        style={{ backgroundColor: categoryColor }}
        aria-hidden
      />
      <div className="p-4 sm:p-5 pl-5 sm:pl-6">{body}</div>
    </article>
  );
}

/**
 * Card browser with breadcrumb drill-in for multi-level parent → child parts.
 */
export const ModificationNestedCards: React.FC<ModificationNestedCardsProps> = ({
  modifications,
  showPricing,
  isOwner,
  onEdit,
  onDelete,
  onMove,
  showSpecs = true,
}) => {
  const forest = useMemo(() => buildModificationForest(modifications), [modifications]);
  const [path, setPath] = useState<string[]>([]);

  useEffect(() => {
    setPath((prev) => {
      if (prev.length === 0) return prev;
      const stillValid = findNodeByPath(forest, prev);
      return stillValid ? prev : [];
    });
  }, [forest]);

  const current = findNodeByPath(forest, path);
  const visibleNodes = current ? current.children : forest;
  const crumbs = useMemo(() => {
    const items: { key: string; title: string; path: string[] }[] = [];
    let level = forest;
    const walked: string[] = [];
    for (const key of path) {
      const node = level.find((n) => nodeKey(n) === key);
      if (!node) break;
      walked.push(key);
      items.push({ key, title: node.mod.title, path: [...walked] });
      level = node.children;
    }
    return items;
  }, [forest, path]);

  const drillIn = (node: HierarchyNode<VehicleDetailModification>) => {
    if (node.children.length === 0) return;
    setPath((prev) => [...prev, nodeKey(node)]);
  };

  const goTo = (nextPath: string[]) => setPath(nextPath);

  return (
    <div className="space-y-4">
      {path.length > 0 && (
        <nav aria-label="Part hierarchy" className="space-y-3">
          <div className="flex flex-wrap items-center gap-1.5 text-sm">
            <button
              type="button"
              onClick={() => goTo([])}
              className="text-muted hover:text-white min-h-[44px] md:min-h-[36px] px-1.5 cursor-pointer transition-colors"
            >
              All parts
            </button>
            {crumbs.map((crumb, i) => (
              <React.Fragment key={crumb.key}>
                <span className="text-gray-600" aria-hidden>
                  /
                </span>
                {i === crumbs.length - 1 ? (
                  <span className="text-white font-medium truncate max-w-[12rem] sm:max-w-xs">
                    {crumb.title}
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => goTo(crumb.path)}
                    className="text-muted hover:text-white min-h-[36px] px-1 cursor-pointer transition-colors truncate max-w-[10rem] sm:max-w-[14rem]"
                  >
                    {crumb.title}
                  </button>
                )}
              </React.Fragment>
            ))}
          </div>

          {current && (
            <div className="build-section relative overflow-hidden">
              <span
                className="absolute inset-y-0 left-0 w-[3px]"
                style={{ backgroundColor: getCategoryColor(current.mod.category) }}
                aria-hidden
              />
              <div className="p-4 sm:p-5 pl-5 sm:pl-6 flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex items-start gap-3">
                  <ModThumb url={current.mod.image_url} alt="" size="md" />
                  <div className="min-w-0">
                    <p className="text-meta mb-1">Inside this part</p>
                    <h3 className="heading-card text-xl">{current.mod.title}</h3>
                    {current.mod.vendor && (
                      <p className="text-sm text-muted mt-1">{current.mod.vendor}</p>
                    )}
                    <p className="text-meta mt-2 tabular-nums">
                      {current.children.length} direct · {countChildren(current)} total nested
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {isOwner && (
                    <OwnerActions
                      mod={current.mod}
                      onEdit={onEdit}
                      onDelete={onDelete}
                      onMove={onMove}
                    />
                  )}
                  <button
                    type="button"
                    onClick={() => goTo(path.slice(0, -1))}
                    className="btn-ghost btn-sm"
                  >
                    Back
                  </button>
                </div>
              </div>
            </div>
          )}
        </nav>
      )}

      {path.length === 0 && forest.some((n) => n.children.length > 0) && (
        <p className="hidden md:block text-sm text-muted">
          Open a part with nested pieces to drill into parent and child levels.
        </p>
      )}

      {visibleNodes.length === 0 ? (
        <div className="rounded-xl border border-dashed border-white/[0.06] px-4 py-10 text-center">
          <p className="text-sm text-white mb-1">No nested parts here</p>
          <p className="text-meta">This part has no children at this level.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 md:gap-4">
          {visibleNodes.map((node) => (
            <PartCard
              key={nodeKey(node)}
              node={node}
              showPricing={showPricing}
              isOwner={isOwner}
              onEdit={onEdit}
              onDelete={onDelete}
              onMove={onMove}
              showSpecs={showSpecs}
              costIncluded={path.length > 0}
              onOpen={node.children.length > 0 ? () => drillIn(node) : undefined}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ModificationNestedCards;
